// ActionMenu with custom copy-to-clipboard functionality

// Expects the following Contentful entries:
// - [Brand] Generic Group
//   - First item: [Brand] Generic Content with Heading (aria label) + Link
//   - Subsequent items: [Brand] Generic Content with Heading + Subheading (copyable)

import {useEffect, useState, type ComponentProps} from 'react'
import {ActionMenu, Button, Text} from '@primer/react-brand/esm'
import {findAllByContentType, type GenericGroup} from '../../../../../brand/lib/types/contentful'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {announce} from '@github-ui/aria-live'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {CopyIcon} from '@primer/octicons-react'

import styles from './CommandActionMenu.module.css'

type CopyStatus = 'copied' | 'failed'

const getInstallAnalytics = (action: string, location: string) =>
  getAnalyticsEvent({action, tag: 'button', context: 'CTAs', location})

type Props = {
  component: GenericGroup
  location: string
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
}

export function CommandActionMenu({component, location, variant = 'secondary'}: Props) {
  const allItems = findAllByContentType(component.fields.content, 'genericContent')
  const link = allItems[0]?.fields.link?.fields
  const ariaLabel = allItems[0]?.fields.heading || t('Install options')
  const items = allItems.flatMap(item =>
    item.fields.subheading ? [{item, textToCopy: documentToPlainTextString(item.fields.subheading)}] : [],
  )

  const [copyResult, setCopyResult] = useState<{itemId: string; status: CopyStatus} | undefined>()

  useEffect(() => {
    if (!copyResult) return
    const timeout = setTimeout(() => setCopyResult(undefined), 2000)
    return () => clearTimeout(timeout)
  }, [copyResult])

  async function copyToClipboard(itemId: string, value: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopyResult({itemId, status: 'copied'})
      announce(t('Copied to clipboard'))
    } catch {
      setCopyResult({itemId, status: 'failed'})
      announce(t('Could not copy to clipboard'))
    }
  }

  if (!link || items.length === 0) return null

  const statusLabels: Record<CopyStatus, string> = {copied: t('Copied!'), failed: t('Copy failed')}

  return (
    <>
      {/* Desktop */}
      <div className={styles.actionMenu}>
        <ActionMenu mode="split-button">
          <ActionMenu.Button as="a" href={link.href} variant={variant} {...getInstallAnalytics(link.text, location)}>
            {link.text}
          </ActionMenu.Button>

          <ActionMenu.Overlay aria-label={ariaLabel}>
            {items.map(({item, textToCopy}) => {
              const itemStatus = copyResult?.itemId === item.sys.id ? copyResult.status : undefined

              return (
                <ActionMenu.Item
                  key={item.sys.id}
                  as="a"
                  className={styles.item}
                  {...getInstallAnalytics(item.fields.heading ?? textToCopy, location)}
                  // Custom click handler to copy text to clipboard instead of following the link
                  href="#"
                  onClick={event => {
                    event.preventDefault()
                    void copyToClipboard(item.sys.id, textToCopy)
                  }}
                >
                  <Text className={styles.heading} variant="muted">
                    {itemStatus ? statusLabels[itemStatus] : item.fields.heading}
                  </Text>

                  <Text font="monospace">{textToCopy}</Text>

                  <CopyIcon className={styles.icon} aria-hidden />
                </ActionMenu.Item>
              )
            })}
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>

      {/* Mobile */}
      <Button
        as="a"
        block
        href={link.href}
        variant={variant}
        {...getInstallAnalytics(link.text, location)}
        className={styles.mobileButton}
      >
        {link.text}
      </Button>
    </>
  )
}
